import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid2 as Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import {
  Receipt,
  Download,
  Visibility,
  AttachMoney,
  Assessment,
  TrendingUp,
  Refresh,
  CheckCircle,
  Schedule,
  ArrowBack,
} from "@mui/icons-material";
// Charts temporarily removed due to dependency issues
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip as RechartsTooltip,
//   ResponsiveContainer,
//   PieChart,
//   Pie,
//   Cell,
// } from "recharts";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loadStatements } from "../../../../store/actions/ppActions";

const PpStatementsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const ppState = useSelector((state) => state[currentSession]?.pp);
  const statements = ppState?.finance?.statements || [];
  const loading = ppState?.finance?.loading || false;

  const [selectedStatement, setSelectedStatement] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  useEffect(() => {
    dispatch(loadStatements());
  }, [dispatch]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      finalized: { color: "success", label: "Finalized" },
      draft: { color: "warning", label: "Draft" },
      processing: { color: "info", label: "Processing" },
    };

    const config = statusConfig[status] || { color: "default", label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const formatMonth = (monthString) => {
    return new Date(monthString + "-01").toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
  };

  // Prepare chart data
  const chartData = statements.map((statement) => ({
    month: new Date(statement.month + "-01").toLocaleDateString("en-US", {
      month: "short",
    }),
    revenue: statement.totalRevenue,
    share: statement.ppShare,
    planReview: statement.services.planReview.revenue,
    inspections: statement.services.inspections.revenue,
  }));

  const totalRevenue = statements.reduce((sum, s) => sum + s.totalRevenue, 0);
  const totalPpShare = statements.reduce((sum, s) => sum + s.ppShare, 0);
  const avgMonthlyRevenue =
    statements.length > 0 ? totalRevenue / statements.length : 0;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate("/pp/finance")}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography color="primary" variant="h4">
            Monthly Statements
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => dispatch(loadStatements())}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    Total Revenue
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {formatCurrency(totalRevenue)}
                  </Typography>
                </Box>
                <AttachMoney sx={{ fontSize: 40, color: "primary.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    Total Earnings
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {formatCurrency(totalPpShare)}
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: "success.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    Avg Monthly
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {formatCurrency(avgMonthlyRevenue)}
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, color: "info.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    Commission Rate
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    30%
                  </Typography>
                </Box>
                <Assessment sx={{ fontSize: 40, color: "warning.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Revenue Trend - Simplified without recharts */}
      {chartData.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Monthly Revenue Summary
            </Typography>
            <Grid container spacing={2}>
              {chartData.map((data, index) => (
                <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {data.month}
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(data.revenue)}
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      Share: {formatCurrency(data.share)}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Statements Table */}
      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Statement</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Services</TableCell>
                <TableCell>Total Revenue</TableCell>
                <TableCell>Your Share</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Loading statements...
                  </TableCell>
                </TableRow>
              ) : statements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No statements found
                  </TableCell>
                </TableRow>
              ) : (
                statements.map((statement) => (
                  <TableRow key={statement.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {statement.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatMonth(statement.month)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {statement.services.planReview.count} Plan Reviews
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {statement.services.inspections.count} Inspections
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(statement.totalRevenue)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="primary"
                      >
                        {formatCurrency(statement.ppShare)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        30% share
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(statement.status)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedStatement(statement);
                              setDetailsDialogOpen(true);
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download PDF">
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={statement.status === "draft"}
                          >
                            <Download />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Statement Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Receipt />
            Statement Details - {selectedStatement?.id}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedStatement && (
            <Box>
              {/* Header Info */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Period
                  </Typography>
                  <Typography variant="h6">
                    {formatMonth(selectedStatement.month)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {getStatusChip(selectedStatement.status)}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Revenue
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(selectedStatement.totalRevenue)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Your Share (30%)
                  </Typography>
                  <Typography variant="h5" color="primary">
                    {formatCurrency(selectedStatement.ppShare)}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Service Breakdown */}
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h6" gutterBottom>
                    Service Breakdown
                  </Typography>
                  <List>
                    <ListItem divider>
                      <ListItemText
                        primary="Plan Review Services"
                        secondary={`${selectedStatement.services.planReview.count} services`}
                      />
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(
                            selectedStatement.services.planReview.revenue
                          )}
                        </Typography>
                        <Typography variant="caption" color="success.main">
                          Share:{" "}
                          {formatCurrency(
                            selectedStatement.services.planReview.ppShare
                          )}
                        </Typography>
                      </Box>
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Inspection Services"
                        secondary={`${selectedStatement.services.inspections.count} services`}
                      />
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(
                            selectedStatement.services.inspections.revenue
                          )}
                        </Typography>
                        <Typography variant="caption" color="success.main">
                          Share:{" "}
                          {formatCurrency(
                            selectedStatement.services.inspections.ppShare
                          )}
                        </Typography>
                      </Box>
                    </ListItem>
                  </List>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h6" gutterBottom>
                    Revenue Distribution
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Plan Review Services"
                        secondary={`${((selectedStatement.services.planReview.revenue / selectedStatement.totalRevenue) * 100).toFixed(1)}% of total`}
                      />
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(
                          selectedStatement.services.planReview.revenue
                        )}
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Inspection Services"
                        secondary={`${((selectedStatement.services.inspections.revenue / selectedStatement.totalRevenue) * 100).toFixed(1)}% of total`}
                      />
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(
                          selectedStatement.services.inspections.revenue
                        )}
                      </Typography>
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              {selectedStatement.status === "draft" && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  This statement is still in draft status. Final amounts may
                  change before finalization.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          {selectedStatement?.status === "finalized" && (
            <Button variant="contained" startIcon={<Download />}>
              Download PDF
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PpStatementsPage;
